import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0.00';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const PriceListPage = () => {
  const { hasPermission } = useAuth();
  const canDelete = hasPermission('manage_price_lists');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [priceList, setPriceList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [price, setPrice] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchPriceList = async (customerId) => {
    try {
      const response = await axios.get(`${API_URL}/price-lists/customer/${customerId}`);
      setPriceList(response.data);
    } catch (error) {
      toast.error('Failed to load price list');
    }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomer(customerId);
    setSelectedIds([]);
    fetchPriceList(customerId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/price-lists`, {
        customer_id: selectedCustomer,
        product_id: selectedProduct,
        price: parseFloat(price)
      });
      toast.success('Price added successfully');
      setDialogOpen(false);
      setSelectedProduct('');
      setPrice('');
      fetchPriceList(selectedCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add price');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setEditPrice(item.price.toString());
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/price-lists/${selectedCustomer}/${editingItem.product_id}?price=${parseFloat(editPrice)}`
      );
      toast.success('Price updated successfully');
      setEditDialogOpen(false);
      setEditingItem(null);
      setEditPrice('');
      fetchPriceList(selectedCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update price');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to remove this custom price?')) return;
    try {
      await axios.delete(`${API_URL}/price-lists/${selectedCustomer}/${productId}`);
      toast.success('Price removed successfully');
      fetchPriceList(selectedCustomer);
    } catch (error) {
      toast.error('Failed to remove price');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Remove ${selectedIds.length} custom prices?`)) return;
    
    try {
      await axios.delete(`${API_URL}/price-lists/bulk/${selectedCustomer}`, { data: selectedIds });
      toast.success(`Removed ${selectedIds.length} custom prices`);
      setSelectedIds([]);
      fetchPriceList(selectedCustomer);
    } catch (error) {
      toast.error('Failed to remove prices');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredPriceList.map(item => item.product_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (productId, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, productId]);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== productId));
    }
  };

  const filteredPriceList = priceList.filter(item => 
    item.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.product?.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="price-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">Price Lists</h1>
          <p className="text-muted-foreground">Manage customer-specific pricing</p>
        </div>
        {selectedIds.length > 0 && canDelete && (
          <Button variant="destructive" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Remove ({selectedIds.length})
          </Button>
        )}
      </div>

      <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
        <label className="block text-sm font-medium mb-2">Select Customer</label>
        <Select value={selectedCustomer} onValueChange={handleCustomerChange}>
          <SelectTrigger className="max-w-md" data-testid="price-list-customer-select">
            <SelectValue placeholder="Choose a customer to view their price list" />
          </SelectTrigger>
          <SelectContent>
            {customers.map(customer => (
              <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCustomer && (
        <>
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 flex items-center gap-2 bg-white p-3 rounded-xl border border-border">
              <Search className="w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-0 focus-visible:ring-0"
                data-testid="price-list-search"
              />
            </div>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
              data-testid="add-price-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Price
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="price-list-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-4 text-left">
                      <Checkbox 
                        checked={selectedIds.length === filteredPriceList.length && filteredPriceList.length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Product</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Base Price</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Customer Price</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Discount</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredPriceList.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                        No custom prices set for this customer. They will use base prices.
                      </td>
                    </tr>
                  )}
                  {filteredPriceList.map((item, index) => {
                    const discount = ((item.product.base_price - item.price) / item.product.base_price * 100).toFixed(1);
                    return (
                      <tr key={index} className="hover:bg-muted/30 transition-colors" data-testid="price-list-row">
                        <td className="px-4 py-4">
                          <Checkbox 
                            checked={selectedIds.includes(item.product_id)}
                            onCheckedChange={(checked) => handleSelectOne(item.product_id, checked)}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-foreground">{item.product.name}</td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">{item.product.category}</td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">RWF {formatNumber(item.product.base_price)}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-foreground">RWF {formatNumber(item.price)}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={discount > 0 ? 'text-green-600' : discount < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                            {discount > 0 ? '-' : '+'}{Math.abs(discount)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid="edit-price-btn">
                              <Edit className="w-4 h-4" />
                            </Button>
                            {canDelete && (
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(item.product_id)} data-testid="delete-price-btn">
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add Price Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="price-dialog">
          <DialogHeader>
            <DialogTitle>Add Custom Price</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Product</label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger data-testid="price-product-select">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} (Base: RWF {formatNumber(product.base_price)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Custom Price (RWF)</label>
              <Input
                type="number"
                step="0.01"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Enter custom price"
                data-testid="price-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="price-submit-btn">
              Save Price
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Price Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-price-dialog">
          <DialogHeader>
            <DialogTitle>Edit Custom Price</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            {editingItem && (
              <>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="font-medium">{editingItem.product?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Base Price: RWF {formatNumber(editingItem.product?.base_price)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">New Custom Price (RWF)</label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    data-testid="edit-price-input"
                  />
                </div>
              </>
            )}
            <Button type="submit" className="w-full" data-testid="edit-price-submit-btn">
              Update Price
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceListPage;
